import React, { useEffect, useRef } from 'react';

interface ArtistPromptModalProps {
  onYes: () => void;
  onNo: () => void;
  isOpen: boolean;
}

const ArtistPromptModal: React.FC<ArtistPromptModalProps> = ({ onYes, onNo, isOpen }) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;

    window.setTimeout(() => {
      headingRef.current?.focus();
    }, 0);

    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen]);

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onNo();
      return;
    }

    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;

    const focusables = Array.from(
      root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);

    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div ref={dialogRef} onKeyDown={onKeyDown}>
      {/* Dismiss X button */}
      <button
        type="button"
        onClick={onNo}
        className="absolute right-4 top-4 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label="Close"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="px-6 pb-8 pt-10 sm:px-10 sm:pb-10 sm:pt-12 text-center">
        {/* Illustration */}
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-200 shadow-inner">
          <svg
            className="h-10 w-10 text-orange-600"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.174 6.812a1 1 0 00-3.986-3.987L3.842 16.174a2 2 0 00-.5.83l-1.321 4.352a.5.5 0 00.623.622l4.353-1.32a2 2 0 00.83-.497z" />
            <path d="M14.5 12.5l1.5 1.5" />
            <path d="M18 9l1-1" />
            <path d="M19.5 4.5l-3 3" />
            <path d="M16 2l-3 3" />
          </svg>
        </div>

        <h2
          id="artist-prompt-title"
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
        >
          Welcome to InkVoyd!
        </h2>

        <p className="mt-3 text-base text-gray-600 leading-relaxed">
          Are you an artist looking to showcase your work and connect with clients?
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onYes}
            className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/25 hover:from-orange-600 hover:to-amber-600 hover:shadow-orange-500/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
          >
            Yes, I&apos;m an artist
          </button>
          <button
            type="button"
            onClick={onNo}
            className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-8 py-3.5 text-base font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            No, I&apos;m just browsing
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtistPromptModal;
